import { QueryObserverResult, UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createComponent,
  createDataEntryComponent,
  createPage,
  createPageTemplate,
  deleteComponent,
  deleteDataEntryComponent,
  deletePage,
  deletePageTemplate,
  fetchPages,
  listPageTemplates,
  updateComponent,
  updateDataEntryComponent,
  updatePage,
  updatePageTemplate,
} from './pages-client';
import type { Component, DataEntryComponent, Page, PageTemplate, PageTemplateWritePayload } from './pages-client.type';

/**
 * Query manager for fetching a list of pages for the current user.
 *
 * @param options UseQueryOptions
 * @returns a object of pages along with the query manager
 */
const usePages = (
  organizationId: string,
  portfolioId: string,
  type: string,
  moduleType: string,
  options?: Partial<UseQueryOptions<Page[]>>,
): {
  pages: Page[];
  query: QueryObserverResult<Page[]>;
} => {
  const query = useQuery({
    queryKey: ['pages', organizationId, portfolioId, type, moduleType],
    queryFn: () => fetchPages(organizationId, portfolioId, type, moduleType),
    ...options,
  });

  let pages: Page[] = [];
  if (Array.isArray(query.data) && query.data.length > 0) {
    pages = query.data.sort((a, b) => a.name.localeCompare(b.name));
  }

  return { pages: pages || [], query };
};
/**
 * Query manager for creating a component.
 */
const useCreateComponent = () => {
  return useMutation({
    mutationFn: ({ component }: { component: Component }) => createComponent(component),
  });
};

/**
 * Query manager for updating a component.
 */
const useUpdateComponent = () => {
  return useMutation({
    mutationFn: ({ componentId, component }: { componentId: string; component: Component }) =>
      updateComponent(componentId, component),
  });
};

/**
 * Query manager for deleting a component.
 */
const useDeleteComponent = () => {
  return useMutation({
    mutationFn: ({ componentId }: { componentId: string }) => deleteComponent(componentId),
  });
};

/**
 * Query manager for creating a page.
 *
 * @returns Page
 */
const useCreatePage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createPage,
    onSuccess: () => {
      // Invalidate the pages query to refetch the updated list
      queryClient.invalidateQueries({ queryKey: ['pages'] });
    },
  });
};

/**
 * Query manager for updating a page.
 */
const useUpdatePage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string | undefined; payload: Page }) =>
      id ? updatePage(id, payload) : Promise.reject(new Error('Page ID is required')),
    onSuccess: () => {
      // Invalidate the pages query to refetch the updated list
      queryClient.invalidateQueries({ queryKey: ['pages'] });
    },
  });
};

/**
 * Query manager for deleting a page.
 */
const useDeletePage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePage,
    onSuccess: () => {
      // Invalidate the pages query to refetch the updated list
      queryClient.invalidateQueries({ queryKey: ['pages'] });
    },
  });
};

/**
 * Query manager for creating a page template.
 */
const useCreatePageTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ page_id, payload }: { page_id: string; payload: PageTemplateWritePayload }) =>
      createPageTemplate(page_id, payload),
    onSuccess: () => {
      // Invalidate the page templates query to refetch the updated list
      queryClient.invalidateQueries({ queryKey: ['page-templates'] });
    },
  });
};

/**
 * Query manager for fetching a list of page templates for the current user.
 *
 * @param {string} organizationId - The ID of the organization to filter templates by
 * @param {string | undefined} portfolioId - The ID of the portfolio to filter templates by
 * @returns a object of page templates along with the query manager
 */
const usePageTemplates = (
  organizationId: string,
  portfolioId?: string,
): {
  pageTemplates: PageTemplate[];
  query: QueryObserverResult<PageTemplate[]>;
} => {
  const query = useQuery({
    queryKey: ['page-templates', organizationId, portfolioId],
    queryFn: () => listPageTemplates(organizationId, portfolioId),
  });

  return { pageTemplates: query.data || [], query };
};

/**
 * Query manager for deleting a page template.
 */
const useDeletePageTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePageTemplate,
    onSuccess: () => {
      // Invalidate the page templates query to refetch the updated list
      queryClient.invalidateQueries({ queryKey: ['page-templates'] });
    },
  });
};

/**
 * Query manager for updating a page template.
 *
 * When the payload includes `config`, the backend cascades to child pages —
 * so we also invalidate the `pages` cache to surface updated child dashboards.
 */
const useUpdatePageTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: PageTemplateWritePayload }) => updatePageTemplate(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['page-templates'] });
      if (variables.payload.config) {
        queryClient.invalidateQueries({ queryKey: ['pages'] });
      }
    },
  });
};

/**
 * Query manager for creating a data entry component.
 */
const useCreateDataEntryComponent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ payload }: { payload: DataEntryComponent }) => createDataEntryComponent(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['data-entry-components'] });
    },
  });
};

/**
 * Query manager for updating a data entry component.
 */
const useUpdateDataEntryComponent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: DataEntryComponent }) => updateDataEntryComponent(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['data-entry-components'] });
    },
  });
};

/**
 * Query manager for deleting a data entry component.
 */
const useDeleteDataEntryComponent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string }) => deleteDataEntryComponent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['data-entry-components'] });
    },
  });
};

export {
  useCreateComponent,
  useCreateDataEntryComponent,
  useCreatePage,
  useCreatePageTemplate,
  useDeleteComponent,
  useDeleteDataEntryComponent,
  useDeletePage,
  useDeletePageTemplate,
  usePageTemplates,
  usePages,
  useUpdateComponent,
  useUpdateDataEntryComponent,
  useUpdatePage,
  useUpdatePageTemplate,
};
